'use client'

import { useEffect, useState } from 'react'
import { apiFetch } from '@/lib/api-fetch'
import {
  DEFAULT_SHIPPING_SETTINGS,
  SHIPPING_TIMEZONES,
  type ShippingSettings,
} from '@/lib/shipping-schedule'

const WEEKDAYS = [
  { value: 1, label: 'Пн' },
  { value: 2, label: 'Вт' },
  { value: 3, label: 'Ср' },
  { value: 4, label: 'Чт' },
  { value: 5, label: 'Пт' },
  { value: 6, label: 'Сб' },
  { value: 7, label: 'Вс' },
]

export function ShippingOriginSettings() {
  const [settings, setSettings] = useState<ShippingSettings>(DEFAULT_SHIPPING_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const origin = settings.origins[0]

  useEffect(() => {
    let cancelled = false
    apiFetch('/api/settings/shipping-origin')
      .then(async (response) => {
        const body = await response.json()
        if (!response.ok) throw new Error(body.error || 'Не удалось загрузить точку отправки')
        if (!cancelled) setSettings(body.settings)
      })
      .catch((reason) => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Ошибка загрузки') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  function updateOrigin(patch: Partial<typeof origin>) {
    setSettings((current) => ({
      origins: [{ ...current.origins[0], ...patch }, ...current.origins.slice(1)],
    }))
    setSaved(false)
  }

  function toggleWeekday(day: number) {
    const weekdays = origin.weekdays.includes(day)
      ? origin.weekdays.filter((value) => value !== day)
      : [...origin.weekdays, day].sort((left, right) => left - right)
    if (weekdays.length > 0) updateOrigin({ weekdays })
  }

  async function save() {
    if (saving) return
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      const response = await apiFetch('/api/settings/shipping-origin', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body.error || 'Не удалось сохранить расписание')
      setSettings(body.settings)
      setSaved(true)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="mb-12">
      <div className="mb-5">
        <h2 className="text-[18px] font-semibold tracking-tight text-ink">Отправка</h2>
        <div className="mt-0.5 text-[13px] text-ink-muted">Откуда и когда заказы передаются в СДЭК</div>
      </div>
      <div className="surface-card rounded-md p-4 sm:p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-[12px] font-medium text-ink-2">
            Название точки
            <input className="input mt-1.5 w-full" value={origin.name} onChange={(event) => updateOrigin({ name: event.target.value })} disabled={loading} />
          </label>
          <label className="text-[12px] font-medium text-ink-2">
            Город
            <input className="input mt-1.5 w-full" value={origin.city} onChange={(event) => updateOrigin({ city: event.target.value })} disabled={loading} />
          </label>
          <label className="text-[12px] font-medium text-ink-2">
            Регион
            <input className="input mt-1.5 w-full" value={origin.region} onChange={(event) => updateOrigin({ region: event.target.value })} disabled={loading} />
          </label>
          <label className="text-[12px] font-medium text-ink-2">
            Часовой пояс
            <select className="input mt-1.5 w-full" value={origin.timezone} onChange={(event) => updateOrigin({ timezone: event.target.value })} disabled={loading}>
              {SHIPPING_TIMEZONES.map((timezone) => <option key={timezone.value} value={timezone.value}>{timezone.label}</option>)}
            </select>
            <span className="mt-1.5 block text-[11px] font-normal leading-relaxed text-ink-faint">
              Используется местное время точки отправки. Для Юрги это Кемеровская область, UTC+7.
            </span>
          </label>
        </div>

        <div className="mt-5 border-t border-rule pt-5">
          <div className="text-[12px] font-semibold text-ink">Дни отправки</div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {WEEKDAYS.map((day) => {
              const active = origin.weekdays.includes(day.value)
              return (
                <button key={day.value} type="button" onClick={() => toggleWeekday(day.value)} className={`h-9 min-w-10 rounded-full px-3 text-[11px] font-semibold transition ${active ? 'bg-onyx text-ivory' : 'bg-bone text-ink-muted'}`}>
                  {day.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="mt-5 border-t border-rule pt-5">
          <div className="text-[12px] font-semibold text-ink">Время рейсов по {origin.city || 'точке отправки'}</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {origin.dispatchTimes.map((time, index) => (
              <div key={`${index}:${time}`} className="flex items-center gap-1.5">
                <input
                  type="time"
                  className="input w-[112px] tab-nums"
                  value={time}
                  onChange={(event) => updateOrigin({ dispatchTimes: origin.dispatchTimes.map((value, itemIndex) => itemIndex === index ? event.target.value : value) })}
                />
                {origin.dispatchTimes.length > 1 && (
                  <button type="button" onClick={() => updateOrigin({ dispatchTimes: origin.dispatchTimes.filter((_, itemIndex) => itemIndex !== index) })} className="size-9 rounded-full text-[18px] text-ink-faint hover:bg-bone hover:text-ink" aria-label={`Удалить рейс ${time}`}>×</button>
                )}
              </div>
            ))}
            {origin.dispatchTimes.length < 8 && (
              <button type="button" onClick={() => updateOrigin({ dispatchTimes: [...origin.dispatchTimes, '12:00'] })} className="btn btn-sm btn-ghost">Добавить время</button>
            )}
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-ink-faint">Расписание показывает ближайший плановый рейс. Факт передачи подтверждается только новым статусом СДЭК.</p>
        </div>

        <div className="mt-5 border-t border-rule pt-5">
          <label className="block text-[12px] font-semibold text-ink">
            Время на сбор заказа
            <input
              type="number"
              min={0}
              max={480}
              step={5}
              inputMode="numeric"
              className="input mt-2 w-full max-w-[156px] tab-nums"
              value={origin.preparationMinutes}
              onChange={(event) => updateOrigin({ preparationMinutes: Math.max(0, Math.min(480, Number(event.target.value) || 0)) })}
              disabled={loading}
            />
            <span className="ml-2 text-[12px] font-normal text-ink-muted">минут</span>
          </label>
          <p className="mt-2 max-w-2xl text-[11px] leading-relaxed text-ink-faint">
            Это минимальное время между оформлением заказа и рейсом: нужно собрать и упаковать изделие. Если до ближайшего рейса остаётся столько же или меньше, предуведомление назначит следующий рейс.
          </p>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-rule pt-5">
          <button type="button" onClick={save} disabled={loading || saving} className="btn btn-primary">{saving ? 'Сохраняем…' : 'Сохранить расписание'}</button>
          {saved && <span className="text-[12px] text-st-delivered">Сохранено</span>}
          {error && <span role="alert" className="text-[12px] text-st-unpaid">{error}</span>}
        </div>
      </div>
    </section>
  )
}
